import { IModel } from '@/@types/models/model';
import { coreFields } from '@/models/helpers/coreFields';
import { commonFields } from './helpers/commonFields';
import { hpsFields } from './helpers/hpsFields';
import { modifiers } from './helpers/modifiers';

const RoleHGSSItems = {
  labels: ['user_roles.hgss_user', 'user_roles.hgss_mod'],
  values: [21, 22],
  default: 21,
};

const RoleHPSItems = {
  labels: [
    'user_roles.hps_user',
    'user_roles.hps_mod',
    'user_roles.hps_pv_user',
    'user_roles.hps_pv_user_view',
    'user_roles.hps_pu_admin',
    'user_roles.hps_spv_admin',
    'user_roles.hps_komisija',
    'user_roles.hps_admin'
  ],
  values: [31, 32, 33, 37, 34, 35, 36, 39],
  default: 31,
};

const OrganisationsHGSSItems = {
  labels: ['organisations.hgss'],
  values: [20],
  default: 20,
};

const OrganisatonsHPSItems = {
  labels: ['organisations.hps'],
  values: [30],
  default: 30,
};

const User: IModel = {
  title: 'Users',
  apiPath: 'users',
  httpUpdateVerb: 'put',
  forms: {
    login: {
      fields: [{ source: 'email', type: 'text' }, 'password'],
    },
    register: {
      fields: [
        { source: 'email', type: 'text', width: 'full' },
        'password',
        'password_confirm',
        'first_name',
        'last_name',
        'oib',
        'mobitel',
        { source: 'svrha_registracije', width: 'full' },
      ],
      validator: 'validatePasswordSet',
    },
    register_hps: {
      fields: ['hps_ime', 'hps_prezime','hps_email','hps_plan_udruga_naziv', 'hps_spv_naziv', 'hps_broj_vodicke_znacke', 'hps_has_licenca', 'hps_licenca_do'],
    },
    register_combined: {
      fields: [
        { source: 'email', type: 'text', width: 'full' },
        'password',
        'password_confirm',
        'first_name',
        'last_name',
        'oib',
        'mobitel',
        { source: 'svrha_registracije', width: 'full' },
        ...modifiers.markReadonlyTrue([
          'hps_ime', 
          'hps_prezime',
          'hps_email',
          'hps_plan_udruga_naziv',
          'hps_spv_naziv',
          'hps_broj_vodicke_znacke',
          'hps_has_licenca',
          'hps_licenca_do',
          'hps_spv_oib',
          'hps_plan_udruga_oib',
        ]),
      ],
      validator: 'validatePasswordSet',
    },
    insert: {
      fields: [
        { source: 'email', type: 'text' },
        'first_name',
        'last_name',
        'oib',
        'mobitel',
        'hps_plan_udruga_id',
        'hps_spv_id',
        'hps_broj_vodicke_znacke',
        'hps_has_licenca',
        'hps_licenca_do',
        'user_role_id',
        'owner_organisation_id',
        'remarks',
        'is_anoniman',
        'password',
        'password_confirm',
      ],
      validator: 'validatePasswordSet',
    },
    insert_hps: {
      fields: [
        { source: 'email', type: 'text' },
        'first_name',
        'last_name',
        'oib',
        'mobitel',
        ...modifiers.markRequired(['hps_plan_udruga_id', 'hps_spv_id']),
        'hps_broj_vodicke_znacke',
        'hps_has_licenca',
        'hps_licenca_do',
        { source: 'user_role_id', items: RoleHPSItems },
        { source: 'owner_organisation_id', items: OrganisatonsHPSItems },
        'remarks',
        'is_anoniman',
        'password',
        'password_confirm',
      ],
      validator: 'validatePasswordSet',
    },
    insert_hgss: {
      fields: [
        { source: 'email', type: 'text' },
        'first_name',
        'last_name',
        'oib',
        'mobitel',
        ...modifiers.markReadonlyTrue(['hps_plan_udruga_id', 'hps_spv_id', 'hps_broj_vodicke_znacke', 'hps_has_licenca', 'hps_licenca_do']),
        { source: 'user_role_id', items: RoleHGSSItems },
        { source: 'owner_organisation_id', items: OrganisationsHGSSItems },
        'remarks',
        'is_anoniman',
        'password',
        'password_confirm',
      ],
      validator: 'validatePasswordSet',
    },
    update: {
      fields: [
        'first_name',
        'last_name',
        'oib',
        { source: 'email', readonly: true },
        'mobitel',
        'hps_plan_udruga_id',
        'hps_spv_id',
        'hps_broj_vodicke_znacke',
        'hps_has_licenca',
        'hps_licenca_do',
        'user_role_id',
        'owner_organisation_id',
        'status',
        'remarks',
        'is_anoniman',
      ],
    },
    update_hps: {
      fields: [
        'first_name',
        'last_name',
        'oib',
        { source: 'email', readonly: true },
        'mobitel',
        ...modifiers.markRequired(['hps_plan_udruga_id', 'hps_spv_id']),
        'hps_broj_vodicke_znacke',
        'hps_has_licenca',
        'hps_licenca_do',
        { source: 'user_role_id', items: RoleHPSItems },
        { source: 'owner_organisation_id', items: OrganisatonsHPSItems },
        'status',
        'remarks',
        'is_anoniman',
      ],
    },
    update_hgss: {
      fields: [
        'first_name',
        'last_name',
        'oib',
        { source: 'email', readonly: true },
        'mobitel',
        ...modifiers.markReadonlyTrue(['hps_plan_udruga_id', 'hps_spv_id', 'hps_broj_vodicke_znacke', 'hps_has_licenca', 'hps_licenca_do']),
        { source: 'user_role_id', items: RoleHGSSItems },
        { source: 'owner_organisation_id', items: OrganisationsHGSSItems },
        'status',
        'remarks',
        'is_anoniman',
      ],
    },
    password_reset_request: {
      fields: [{ source: 'email', readonly: false, type: 'text' }],
    },
    password_reset: {
      fields: [{ source: 'email', readonly: true, type: 'text' }, 'password_new', 'password_new_confirm'],
      validator: 'validatePasswordReset',
    },
    password_change: {
      fields: [{ source: 'password', ttoken: 'users.password_current' }, 'password_new', 'password_new_confirm'],
      validator: 'validatePasswordChange',
    },
    password_set: {
      fields: ['password_new', 'password_new_confirm'],
      validator: 'validatePasswordChange',
    },
    activate: {
      fields: ['email', 'activation_token', 'password', 'password_new', 'password_new_confirm'],
      validator: 'validatePasswordChange',
    },
    planinarski_vodic: {
      fields: ["first_name", "last_name", "oib", "email", "mobitel", "hps_plan_udruga_id", "hps_spv_id", "hps_has_licenca", "hps_licenca_do", "hps_broj_vodicke_znacke"]
    },
    vodic_obrazac: {
      fields: [
        {source: 'first_name', readonly: true}, 
        {source: 'last_name', readonly: true}, 
        {source: 'oib', readonly: true}, 
        {source: 'mobitel', readonly: true}, 
        {source: 'email', readonly: true}, 
        'adresa',
        {source: 'hps_plan_udruga_naziv', readonly: true}, 
        {source: 'hps_spv_naziv', readonly: true}, 
        'hps_broj_vodicke_znacke',
        'pocetak', 
        'kraj', 
        'edukacija'
      ]
    },
  },

  listViews: {
    default: {
      fields: ['id', 'active', 'first_name', 'last_name', 'email', 'created_on', 'status', 'owner_organisation_id', 'user_role_id'],
      hidden: ['id', 'active'],
    },
    admin: {
      fields: ['id', 'active', 'first_name', 'last_name', 'email', 'oib', 'created_on', 'status', 'owner_organisation_id', 'user_role_id'],
      hidden: ['id', 'active'],
    },
    hps: {
      fields: [
        'id',
        'first_name',
        'last_name',
        'email',
        'oib',
        'user_role_id',
        'hps_plan_udruga_id',
        'hps_spv_id',
        'hps_broj_vodicke_znacke',
        'hps_has_licenca',
        'hps_licenca_do',
        'created_on',
        'status',
        'active',
      ],
      hidden: ['id', 'active'],
    },
    hgss: {
      fields: ['id', 'first_name', 'last_name', 'email', 'oib', 'user_role_id', 'created_on', 'status', 'owner_organisation_id', 'active'],
      hidden: ['id', 'active'],
    },
    planinarski_vodici: {
      fields: ['id', 'first_name', 'last_name', 'hps_plan_udruga_id', 'hps_spv_id', 'hps_has_licenca', 'hps_licenca_do', 'hps_izleti_count', 'created_on'],
      hidden: ['id'],
    },
  },
  fields: [
    coreFields.id,
    coreFields.active,
    {
      title: "OIB",
      source: "oib",
      ttoken: "common.oib",
      type: "text",
      validation: {
        required: false, //uncomment later but for now since no users have oib it can be non mandatory
        minLength: 11,
        maxLength: 11,
        function: "oib"
      },
      export: {
        width: 20
      }
    },
    {
      title: 'Ime',
      source: 'first_name',
      ttoken: 'users.first_name',
      type: 'text',
      filter: true,
      validation: {
        required: true,
        maxLength: 50,
      },
    },
    {
      title: 'Prezime',
      source: 'last_name',
      ttoken: 'users.last_name',
      type: 'text',
      filter: true,
      validation: {
        required: true,
        maxLength: 50,
      },
    },
    {
      title: 'Ime i prezime',
      source: 'full_name',
      ttoken: 'users.full_name',
      type: 'text',
    },
    {
      title: 'E-mail',
      source: 'email',
      ttoken: 'users.email',
      type: 'mail',
      filter: true,
      validation: {
        required: true,
        maxLength: 250,
        function: 'email',
      },
    },
    {
      title: 'Status',
      source: 'status',
      ttoken: 'common.status',
      type: 'picker',
      readonly: true,
      filter: true,
      items: {
        labels: ['user_states.registriran', 'user_states.aktivan', 'user_states.onemogucen_hps', 'user_states.neaktivan', 'user_states.anoniman'],
        values: [10, 20, 29, 30, 40],
        default: 20,
      },
      translate: true,
    },
    {
      title: 'Uloga',
      source: 'user_role_id',
      ttoken: 'users.uloga',
      type: 'picker',
      items: {
        labels: [
          'user_roles.hgss_user',
          'user_roles.hgss_mod',
          'user_roles.hps_user',
          'user_roles.hps_mod',
          'user_roles.admin',
          'user_roles.reg_user',
          'user_roles.hps_pv_user',
          'user_roles.hps_pv_user_view',
          'user_roles.hps_pu_admin',
          'user_roles.hps_spv_admin',
          'user_roles.hps_komisija',
          'user_roles.hps_admin'
        ],
        values: [21, 22, 31, 32, 90, 11, 33, 37, 34, 35, 36, 39],
        default: 21,
      },
      validation: {
        required: true,
      },
    },
    {
      title: 'Napomena',
      source: 'remarks',
      ttoken: 'users.napomena',
      type: 'multiline',
      rows: 6,
      validation: {
        maxLength: 250,
      },
      width: 'full',
    },
    {
      title: 'Anonimni korisnik',
      source: 'is_anoniman',
      ttoken: 'users.anoniman',
      readonly: true,
      type: 'text',
      items: {
        labels: ['common.ne', 'common.da'],
        values: [0, 1],
        default: 0,
      },
    },
    {
      title: 'Datum i vrijeme registracije',
      source: 'created_on',
      ttoken: 'common.created_on',
      type: 'datetime',
      filter: true,
      sort: true,
      readonly: true,
    },
    {
      title: 'Aktivacijski Ključ',
      source: 'activation_token',
      ttoken: 'users.activation_token',
      type: 'text',
      readonly: true,
      validation: {
        required: true,
        maxLength: 50,
      },
    },
    {
      title: 'Ključ za izmjenu lozinke',
      source: 'passwordreset_token',
      ttoken: 'users.passwordreset_token',
      type: 'text',
      readonly: true,
      validation: {
        required: true,
        maxLength: 50,
      },
    },
    {
      title: 'Lozinka',
      source: 'password',
      ttoken: 'users.password',
      type: 'password',
      validation: {
        required: true,
      },
    },
    {
      title: 'Potvrda lozinke',
      source: 'password_confirm',
      ttoken: 'users.password_confirm',
      type: 'password',
      validation: {
        required: true,
      },
    },
    {
      title: 'Nova lozinka',
      source: 'password_new',
      ttoken: 'users.password_new',
      type: 'password',
      validation: {
        required: true,
      },
    },
    {
      title: 'Potvrda nove lozinke',
      source: 'password_new_confirm',
      ttoken: 'users.password_new_confirm',
      type: 'password',
      validation: {
        required: true,
      },
    },
    {
      title: 'Organizacija',
      source: 'owner_organisation_id',
      ttoken: 'users.owner_organisation_id',
      type: 'picker',
      items: {
        labels: ['organisations.hgss', 'organisations.hps'],
        values: [20, 30],
        default: 20,
      },
      validation: {
        required: true,
      },
    },
    {
      title: 'Mobitel',
      source: 'mobitel',
      ttoken: 'common.mobitel',
      type: 'text',
      validation: {
        required: false,
        regex: 'tel',
      },
      placeholder: '+385xxxxxxxxx',
    },
    hpsFields.svrha_registracije,
    hpsFields.hps_ime,
    hpsFields.hps_prezime,
    hpsFields.hps_email,
    hpsFields.hps_has_licenca,
    hpsFields.hps_licenca_do,
    hpsFields.hps_broj_vodicke_znacke,
    hpsFields.hps_plan_udruga_id,
    hpsFields.hps_plan_udruga_naziv,
    hpsFields.hps_spv_id,
    hpsFields.hps_spv_naziv,
    hpsFields.hps_izleti_count,
    hpsFields.hps_spv_oib,
    hpsFields.hps_plan_udruga_oib,
    {
      title: "Adresa",
      source: "adresa",
      ttoken: 'users.adresa',
      type: 'text'
    },
    {
      title: "Početak",
      source: "pocetak",
      ttoken: 'izlet.pocetak',
      type: 'date'
    },
    {
      title: "Kraj",
      source: "kraj",
      ttoken: 'izlet.kraj',
      type: 'date'
    },
    {
      // TODO prevesti kako spada
      title: "Sudjelovanje u edukaciji ili obnovi znanja prve pomoći",
      source: "edukacija",
      ttoken: 'users.edukacija',
      type: 'multiline',
      rows: 3,
      width: "full",
      tooltip: "Organizator, mjesto, vrijeme"
    },
  ],
};

export default User;
